import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getUserInfo } from "../services/api";
import "../styles/landing.css";

const SUBTEXTS = [
  "Pickl helps you find what to watch — fast, free, and dead-on accurate.",
  "Pickl knows what you like (and what your friends like too).",
  "One quick quiz. Zero overthinking. Go!",
];

// Add a hook to detect mobile
function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);
  return isMobile;
}

function Typewriter({ className = "" }) {
  const [currentLine, setCurrentLine] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [phase, setPhase] = useState("fade-in"); // 'fade-in', 'idle', 'deleting', 'typing'
  const [charIdx, setCharIdx] = useState(0);
  const timeoutRef = useRef();

  // Fade in on mount
  useEffect(() => {
    setDisplayed("");
    setCharIdx(0);
    setPhase("fade-in");
    // Type in the first line after fade-in
    timeoutRef.current = setTimeout(() => {
      setPhase("typing");
    }, 100); // short delay to allow fade-in
    return () => clearTimeout(timeoutRef.current);
  }, []);

  // Typing effect
  useEffect(() => {
    if (phase === "typing") {
      if (charIdx < SUBTEXTS[currentLine].length) {
        timeoutRef.current = setTimeout(() => {
          setDisplayed(SUBTEXTS[currentLine].slice(0, charIdx + 1));
          setCharIdx(charIdx + 1);
        }, 38); // typing speed (ms per char)
      } else {
        setPhase("idle");
      }
    }
    return () => clearTimeout(timeoutRef.current);
  }, [phase, charIdx, currentLine]);

  // Idle after typing
  useEffect(() => {
    if (phase === "idle") {
      timeoutRef.current = setTimeout(() => {
        setPhase("deleting");
      }, 7000); // 7s idle before deleting
    }
    return () => clearTimeout(timeoutRef.current);
  }, [phase]);

  // Deleting effect
  useEffect(() => {
    if (phase === "deleting") {
      if (charIdx > 0) {
        timeoutRef.current = setTimeout(() => {
          setDisplayed(SUBTEXTS[currentLine].slice(0, charIdx - 1));
          setCharIdx(charIdx - 1);
        }, 22); // deleting speed (ms per char)
      } else {
        // Move to next line
        setCurrentLine((currentLine + 1) % SUBTEXTS.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(timeoutRef.current);
  }, [phase, charIdx, currentLine]);

  // When currentLine changes, reset charIdx
  useEffect(() => {
    if (phase === "typing") {
      setCharIdx(0);
    }
  }, [currentLine]);

  // Only apply fade-in-delay on first mount
  const fadeClass = phase === "fade-in" || currentLine === 0 ? "fade-in-delay" : "";

  return (
    <p className={`hero-subtext ${fadeClass} ${className}`}>
      {displayed}&nbsp;<span className="typewriter-cursor">|</span>
    </p>
  );
}

function VideoParallax() {
  const isMobile = useIsMobile();
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const animationRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentTransform, setCurrentTransform] = useState({ x: 0, y: 0 });
  const [isFocused, setIsFocused] = useState(false);
  const [focusScale, setFocusScale] = useState(isMobile ? 1.28 : 1.18);
  const [targetFocusScale, setTargetFocusScale] = useState(isMobile ? 1.28 : 1.18);
  const lastMouseMoveRef = useRef(Date.now());
  const focusTimeoutRef = useRef(null);

  // Update scale on mobile/desktop switch
  useEffect(() => {
    setFocusScale(isMobile ? 1.28 : 1.18);
    setTargetFocusScale(isMobile ? 1.28 : 1.18);
  }, [isMobile]);

  // Mouse tracking effect (disable on mobile)
  useEffect(() => {
    if (isMobile) return;
    const handleMouseMove = (e) => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = (e.clientX - rect.left - rect.width / 2) / (rect.width / 2);
      const y = (e.clientY - rect.top - rect.height / 2) / (rect.height / 2);
      setMousePosition({ x, y });
      if (isFocused) {
        setIsFocused(false);
        setTargetFocusScale(1.18);
      }
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
      lastMouseMoveRef.current = Date.now();
      focusTimeoutRef.current = setTimeout(() => {
        setIsFocused(true);
        setTargetFocusScale(1.25);
      }, 2000);
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, [isFocused, isMobile]);

  // Animation effect
  useEffect(() => {
    const animate = () => {
      if (!videoRef.current) return;
      if (isMobile) {
        // On mobile, keep video centered and zoomed
        videoRef.current.style.transform = `scale(1.28) translate(0, 0)`;
        return;
      }
      const easing = 0.08;
      const maxOffset = 0.03;
      const targetX = -mousePosition.x * maxOffset;
      const targetY = -mousePosition.y * maxOffset;
      const newX = currentTransform.x + (targetX - currentTransform.x) * easing;
      const newY = currentTransform.y + (targetY - currentTransform.y) * easing;
      setCurrentTransform({ x: newX, y: newY });
      const focusEasing = 0.03;
      const newFocusScale = focusScale + (targetFocusScale - focusScale) * focusEasing;
      setFocusScale(newFocusScale);
      videoRef.current.style.transform = `scale(${newFocusScale}) translate(${newX * 100}%, ${newY * 100}%)`;
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [mousePosition, currentTransform, targetFocusScale, focusScale, isMobile]);

  return (
    <div className="video-scene" ref={wrapperRef}>
      <div className="video-parallax-wrapper">
        <video
          ref={videoRef}
          className="scene-video"
          src="/static/v2_pickl_scene.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onError={(e) => console.error("Video loading error:", e)}
        />
      </div>
    </div>
  );
}

function Landing() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      try {
        setLoading(true);
        const userData = await getUserInfo();
        setUser(userData);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkUser();
  }, []);

  const isLoggedIn = !!user;

  return (
    <div className="landing-root">
      <header className="landing-header">
        <img src={require("../images/pickl_logo_white.png")} alt="Pickl" className="logo" style={{ height: "40px", width: "auto" }} />
        <div className="header-actions">
          {isLoggedIn ? (
            <button className="view-recs-btn" onClick={() => navigate("/")}>
              View Recommendations
            </button>
          ) : (
            <>
              <button className="login-btn" onClick={() => navigate("/auth?mode=login")}>
                Log In
              </button>
              <button className="signup-btn" onClick={() => navigate("/auth?mode=signup")}>
                Sign Up
              </button>
            </>
          )}
        </div>
      </header>

      <main className="landing-main">
        <section className="hero-section">
          {isMobile ? (
            <h1 className="hero-title fade-scale-in">
              You like movies.
              <br />
              Pickl gives good recs.
            </h1>
          ) : (
            <h1 className="hero-title fade-scale-in">You like movies. Pickl gives good recs.</h1>
          )}
          <Typewriter />
          <button
            className="hero-cta-btn fade-in-delay"
            onClick={() => {
              if (isLoggedIn) {
                navigate("/");
              } else {
                navigate("/auth");
              }
            }}
          >
            Get Started with Pickl!
          </button>
        </section>
        {!isMobile && <VideoParallax />}
      </main>

      <footer className="landing-footer">
        <div className="footer-bottom-row">
          <div className="footer-copyright">© Pickl. All rights reserved.</div>
          <div className="footer-links-right">
            <a href="/privacy/" className="footer-link">
              Privacy Policy
            </a>
            <a href="/tos/" className="footer-link">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
